import type { Module } from 'vuex';
import { ICartItem } from '../../entities/cart-entity';

export const namespace = 'cart';

export interface IAddItemToCartBody {
	productId: number;
	modificationId?: number;
	quantity: number;
	data?: any;
}

export interface IUpdateCartItemParams {
	cartItemId: string;
	modificationId?: number;
	quantity: number;
	data?: any;
}

export interface ILoadingState {
	cart: boolean;
	putToCart: boolean;
	updateCartItem: boolean;
	removeCartItem: boolean;
}

export interface ICartState {
	items: ICartItem[];
	loading: ILoadingState;
}

export const MutationType = {
	ADD_CART_ITEM: 'addCartItem',
	SET_ITEM_QUANTITY: 'setItemQuantity',
	SET_CART_ITEMS: 'setCartItems',
	SET_LOADING: 'setLoading',
	REMOVE_CART_ITEM: 'removeCartItem',
};

export const actionType = {
	PUT_TO_CART: 'putToCart',
	UPDATE_CART_ITEM: 'updateCartItem',
	GET_CART: 'getCart',
	REMOVE_CART_ITEM: 'removeCartItem',
};

export default function cartStore(): Module<ICartState, any> {
	return {
		namespaced: true,
		state: () => ({
			items: [],
			loading: {
				putToCart: false,
				cart: false,
				updateCartItem: false,
				removeCartItem: false,
			},
		}),
		actions: {
			async [actionType.GET_CART]({ commit }): Promise<any> {
				try {
					commit('appState/setLoading', { cart: true }, { root: true });
					const res = await this.$api.cart.getCartItems.execute({});
					if (res.isSuccess) {
						const value = res.getValue();
						commit(MutationType.SET_CART_ITEMS, value?.cartItems);
						return value;
					}
					return null;
				} finally {
					commit('appState/setLoading', { cart: false }, { root: true });
				}
			},
			async [actionType.PUT_TO_CART]({ dispatch, commit }, item: IAddItemToCartBody): Promise<boolean> {
				try {
					commit(MutationType.SET_LOADING, { key: 'putToCart', value: true });
					commit('appState/setLoading', { putToCart: true }, { root: true });

					const res = await this.$api.cart.addItemToCart.execute(item);
					if (res.isSuccess) {
						const value = res.getValue();
						if (value?.sessionToken) {
							const sessionCookie = this.$cookies.get('sessionToken');

							if (!sessionCookie) {
								this.$cookies.set('sessionToken', value.sessionToken);
							}
							dispatch('userAuth/SET_SESSION_TOKEN', value.sessionToken, { root: true });
						}
					}
					return res.isSuccess;
				} catch (e) {
					// eslint-disable-next-line no-console
					console.log('error ', e);
					return false;
				} finally {
					commit(MutationType.SET_LOADING, { key: 'putToCart', value: false });
					commit('appState/setLoading', { putToCart: false }, { root: true });
				}
			},
			async [actionType.UPDATE_CART_ITEM]({ commit }, params: IUpdateCartItemParams): Promise<boolean> {
				try {
					commit(MutationType.SET_LOADING, { key: 'updateCartItem', value: true });
					commit('appState/setLoading', { updateCartItem: true }, { root: true });
					const res = await this.$api.cart.updateCartItem.execute(params);
					if (res.isSuccess) {
						commit(MutationType.SET_ITEM_QUANTITY, {
							id: params.cartItemId,
							quantity: params.quantity,
						});
						return true;
					}
					return false;
				} catch (e) {
					return false;
				} finally {
					commit(MutationType.SET_LOADING, { key: 'updateCartItem', value: false });
					commit('appState/setLoading', { updateCartItem: false }, { root: true });
				}
			},
			async [actionType.REMOVE_CART_ITEM]({ commit, dispatch }, id: string): Promise<boolean> {
				try {
					commit(MutationType.SET_LOADING, { key: 'removeCartItem', value: true });
					commit('appState/setLoading', { removeCartItem: true }, { root: true });

					const res = await this.$api.cart.removeItemFromCart.execute({ cartItemId: id });
					if (res.isSuccess) {
						commit(MutationType.REMOVE_CART_ITEM, id);
						return res.isSuccess;
					}
					return false;
				} catch (e) {
					dispatch(
						'appUi/showNotificaton',
						{
							type: 'error',
							message: e ? (e as { message: string }).message : e,
						},
						{ root: true },
					);
					return false;
				} finally {
					commit(MutationType.SET_LOADING, { key: 'removeCartItem', value: false });
					commit('appState/setLoading', { removeCartItem: false }, { root: true });
				}
			},
		},
		mutations: {
			[MutationType.SET_ITEM_QUANTITY](state, { id, quantity }: { id: string; quantity: number }) {
				const idx = state.items.findIndex((i) => i.id === id);
				if (idx > -1) {
					const update = {
						...state.items[idx],
						id,
						quantity,
					};
					state.items.splice(idx, 1, update);
				}
			},
			[MutationType.SET_LOADING]: (state, { key, value }: { key: keyof ILoadingState; value: boolean }) => {
				state.loading[key] = value;
			},
			[MutationType.SET_CART_ITEMS]: (state, items: ICartItem[]) => {
				state.items = items;
			},
			[MutationType.REMOVE_CART_ITEM]: (state, id: string) => {
				const idx = state.items.findIndex((i) => i.id === id);
				if (idx > -1) {
					state.items.splice(idx, 1);
				}
			},
		},
		getters: {
			items: ({ items }) => items,
			totalSum: ({ items }) =>
				items.reduce((sum: number, item: ICartItem) => {
					// const total = sum + (item.sum * item.quantity); считается некоректно, так как SUM это уже сумма всех товаров
					const total = sum + item.price * item.quantity;
					return total;
				}, 0),
			discount: () => 0,
			itemsCount: ({ items }) => items.reduce((count: number, item: ICartItem) => count + item.quantity, 0),
		},
	};
}
